import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { company, footerNav } from "@/content/site";

const socialLinks = [
  { label: "LinkedIn", href: company.social.linkedin, icon: Linkedin },
  { label: "Facebook", href: company.social.facebook, icon: Facebook },
  { label: "Instagram", href: company.social.instagram, icon: Instagram },
];

export function Footer({ className }: { className?: string }) {
  const { address } = company;
  return (
    <footer className={cn("mt-20 border-t border-brand-100 bg-brand-900 text-brand-100", className)}>
      <div className="container grid min-w-0 gap-10 py-10 sm:py-12 md:grid-cols-4">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-3 font-bold text-white">
            <span className="inline-flex items-center rounded-lg bg-white px-2 py-1.5">
              <img src="/brand/logo.png" alt="" className="h-9 w-auto" />
            </span>
            <span className="min-w-0 break-words">{company.name}</span>
          </div>
          <p className="mt-3 text-sm text-brand-200">{company.tagline}</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 break-words">
                {address.line1}, {address.city}, {address.state} {address.zip}
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0" />
              <a href={`tel:${company.phone.replace(/[^+\d]/g, "")}`}>
                {company.phone}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" />
              <a href={`mailto:${company.email}`} className="min-w-0 break-all">{company.email}</a>
            </li>
          </ul>
          <div className="mt-5 flex items-center gap-2">
            {socialLinks.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={`${company.name} on ${label}`}
                aria-label={`${company.name} on ${label}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brand-700 text-brand-200 transition hover:border-accent-500 hover:text-white"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {footerNav.map((col) => (
          <div key={col.heading}>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-white">
              {col.heading}
            </h4>
            <ul className="mt-3 space-y-2 text-sm">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-brand-800 py-4">
        <div className="container flex flex-col gap-2 text-xs text-brand-300 sm:flex-row sm:items-center sm:justify-between">
          <span>
            &copy; {new Date().getFullYear()} {company.name}. All rights reserved.
          </span>
          {/* Design/build credit hidden per owner request 2026-07-06 — leave the
              markup in place so it can be re-enabled without redoing it. */}
          {false && (
            <span>
              Designed &amp; built by{" "}
              <a
                href="https://www.linkedin.com/in/mansurislamov/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand-200 underline-offset-2 transition hover:text-white hover:underline"
              >
                Mansur Islamov
              </a>
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
